import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { completeLogin } from 'services/auth';
import { OAUTH_CODE_VERIFIER, OAUTH_RETURN_TO, OAUTH_STATE } from 'services/auth/constants';
import { exchangeCode } from 'services/auth/token-endpoint';

import OnboardingLayout from '../../layouts/onboarding-layout/onboarding-layout';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  panel: {
    maxWidth: theme.spacing(60),
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  status: {
    marginTop: theme.spacing(2),
  },
  action: {
    display: 'inline-block',
    marginTop: theme.spacing(3),
    color: theme.palette.secondary.main,
  },
}));

const PKCE_KEYS = [OAUTH_STATE, OAUTH_CODE_VERIFIER, OAUTH_RETURN_TO];

const clearPkceKeys = (): void => PKCE_KEYS.forEach((key) => sessionStorage.removeItem(key));

const stripQuery = (): void => window.history.replaceState(null, '', window.location.pathname);

const isSafeReturnTo = (value: string | null): value is string => (
  value !== null && value.startsWith('/') && !value.startsWith('//')
);

const OAuthCallback = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { search } = useLocation();
  const started = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // The authorization code is single-use: never exchange it twice.
    if (started.current) return undefined;
    started.current = true;
    let mounted = true;

    const params = new URLSearchParams(search);
    const code = params.get('code');
    const state = params.get('state');
    const stateSession = params.get('state_session') ?? undefined;
    const storedState = sessionStorage.getItem(OAUTH_STATE);
    const verifier = sessionStorage.getItem(OAUTH_CODE_VERIFIER);
    const returnTo = sessionStorage.getItem(OAUTH_RETURN_TO);
    stripQuery();

    const valid = !params.get('error') && code && verifier && storedState && state === storedState;
    if (!valid) {
      clearPkceKeys();
      history.replace('/sign-in');
      return undefined;
    }

    exchangeCode({ code, verifier, stateSession })
      .then((tokenResponse) => {
        completeLogin(tokenResponse);
        clearPkceKeys();
        if (mounted) history.replace(isSafeReturnTo(returnTo) ? returnTo : '/orders');
      })
      .catch(() => {
        clearPkceKeys();
        if (mounted) setFailed(true);
      });

    return () => { mounted = false; };
  }, [history, search]);

  return (
    <OnboardingLayout>
      <Box className={classes.wrapper}>
        {failed ? (
          <Paper className={classes.panel} role="alert">
            <Typography variant="body1">{t('auth:oauth-error')}</Typography>
            <Link to="/sign-in" className={classes.action}>
              <Typography variant="body2">{t('auth:go-login')}</Typography>
            </Link>
          </Paper>
        ) : (
          <Paper className={classes.panel} aria-busy>
            <CircularProgress color="secondary" size={theme.spacing(4)} />
            <Typography variant="body2" className={classes.status}>{t('auth:redirecting')}</Typography>
          </Paper>
        )}
      </Box>
    </OnboardingLayout>
  );
};

export default OAuthCallback;
